/* SIZESOFTX
=========================================================================================
* Descripción: Login del panel de soporte (acceso master)
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 11/09/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 17/09/2026 | Muse Spark                       | Redirect seguro sin efecto en render.
* ---------------------------------------------------------------------------------------
*/

import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { useSupportAuth } from "../context/SupportAuthContext";
import { sanitizarTexto, tieneContenidoMalicioso } from "../settings/inputSecurity";

const LoginPage = () => {
  const { login, authReady, isAuthenticated } = useSupportAuth();
  const navigate = useNavigate();

  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (authReady && isAuthenticated) {
    return <Navigate to="/resumen" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    const usuarioLimpio = sanitizarTexto(usuario, 50).replace(/\s+/g, "");
    const passwordLimpio = String(password || "").slice(0, 100);
    if (!usuarioLimpio || !passwordLimpio) {
      setError("Captura usuario y contraseña.");
      return;
    }
    if (tieneContenidoMalicioso(usuarioLimpio) || tieneContenidoMalicioso(passwordLimpio)) {
      setError("Credenciales con contenido no permitido.");
      return;
    }
    setLoading(true);
    try {
      const data = await login(usuarioLimpio, passwordLimpio);
      if (data && data.token) {
        navigate("/resumen", { replace: true });
      }
    } catch (err) {
      setError(err.message || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #0b2a4a 0%, #1976d2 100%)",
      }}
    >
      <Card sx={{ minWidth: 380, maxWidth: 420, width: "100%", m: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" align="center" gutterBottom>
            Support SizeSoftX
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" paragraph>
            Acceso restringido a soporte técnico
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label="Usuario"
              variant="outlined"
              fullWidth
              margin="normal"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value.slice(0, 50))}
              autoComplete="username"
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Contraseña"
              type="password"
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value.slice(0, 100))}
              autoComplete="current-password"
              inputProps={{ maxLength: 100 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 3 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Ingresar"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
